/*
 * Edit Round — focused editor for an existing round's setup.
 *
 * Scoped DELIBERATELY narrower than CreateRound:
 *   - Editable here: name, format, scoring mode, points per match,
 *     session type, scheduled start, plus the per-round handicap
 *     overrides (handicap method, allowance %, baseline method).
 *   - NOT editable here: course, tee, course holes. Changing a
 *     course mid-round would invalidate every stored stroke
 *     allocation; if you really need that, recreate the round.
 *
 * Only available while the round is editable (status: draft or
 * published). Once a round is 'locked' / 'in_progress' / 'completed'
 * we redirect back to the detail page.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save } from 'lucide-react';

import { api } from '@/lib/api';
import { decodeRoundSession, encodeRoundSchedule, roundDateYMD, type RoundSession } from '@/lib/roundSchedule';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { InfoHint } from '@/components/InfoHint';
import { TeamStablefordSettings } from '@/components/TeamStablefordSettings';
import { FORMAT_OPTIONS, SCORING_MODE_LABELS, MethodHintBody, BaselineHintBody } from './_shared';
import { teamCupStablefordConfig, type FormatType, type ScoringMode, type RoundSessionType, type StablefordConfig, type TournamentRound } from '@golf-app/shared';

const POINTS_PRESETS = [0.5, 1, 2];

const SESSION_LABELS: Record<RoundSessionType, string> = {
  round:    'Round',
  session:  'Session',
  playoff:  'Playoff',
};

const HANDICAP_OPTIONS = [
  { value: 'course'   as const, label: 'Net Course Rating',    hint: 'USGA conversion using slope + rating to compute pops by tee.' },
  { value: 'straight' as const, label: 'Net Straight Handicap', hint: 'Index plays as-is. 14.4 → 14 strokes on holes 1-14 by stroke index.' },
];

const BASELINE_OPTIONS = [
  { value: 'low_man_zero'    as const, label: 'Low man zero',    hint: 'Lowest playing handicap plays scratch.' },
  { value: 'full_difference' as const, label: 'Full difference', hint: 'Each player gets their full rounded handicap.' },
];

export default function EditRound() {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const navigate = useNavigate();
  const { activeEvent } = useTournamentStore();

  const [round, setRound] = useState<TournamentRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state. 'inherit' on the handicap overrides keeps the round on
  // the event-level default; choosing a specific method overrides.
  const [name, setName] = useState('');
  const [sessionType, setSessionType] = useState<RoundSessionType>('round');
  const [formatType, setFormatType] = useState<FormatType>('four_ball_better_ball');
  const [scoringMode, setScoringMode] = useState<ScoringMode>('net');
  const [stablefordConfig, setStablefordConfig] = useState<StablefordConfig>(() => teamCupStablefordConfig());
  const [stablefordValid, setStablefordValid] = useState(true);
  const [pointsPerMatch, setPointsPerMatch] = useState('1');
  const [scheduledStartAt, setScheduledStartAt] = useState('');
  // Optional Morning/Afternoon tee session, encoded in the stored time
  // of day (see roundSchedule.ts). Never required.
  const [daySession, setDaySession] = useState<RoundSession>('default');
  const [handicapMethod, setHandicapMethod] = useState<'inherit' | 'course' | 'straight'>('inherit');
  const [allowancePct, setAllowancePct] = useState('');     // empty → inherit
  const [baselineMethod, setBaselineMethod] = useState<'inherit' | 'low_man_zero' | 'full_difference'>('inherit');
  const [scrambleLowPlayerPct, setScrambleLowPlayerPct] = useState('35');
  const [scrambleHighPlayerPct, setScrambleHighPlayerPct] = useState('15');

  const load = useCallback(async () => {
    if (!roundId) return;
    try {
      const r = await api.getTournamentRound(roundId);
      setRound(r);
      setName(r.name);
      setSessionType(r.sessionType);
      setFormatType(r.formatType);
      setScoringMode(r.scoringMode);
      setStablefordConfig(r.stablefordConfig ?? teamCupStablefordConfig());
      setPointsPerMatch(String(r.pointsPerMatch ?? 1));
      // Decode in LOCAL time (slicing the raw ISO would give the UTC
      // date, a different calendar day for some local times) and pull
      // the Morning/Afternoon session out of the stored time of day.
      setScheduledStartAt(r.scheduledStartAt ? roundDateYMD(r.scheduledStartAt) : '');
      setDaySession(decodeRoundSession(r.scheduledStartAt));
      setHandicapMethod(r.handicapMethod ?? 'inherit');
      setAllowancePct(r.allowancePct != null ? String(r.allowancePct) : '');
      setBaselineMethod(r.baselineMethod ?? 'inherit');
      setScrambleLowPlayerPct(String(r.scrambleLowPlayerPct ?? 35));
      setScrambleHighPlayerPct(String(r.scrambleHighPlayerPct ?? 15));
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load round");
    } finally {
      setLoading(false);
    }
  }, [roundId]);

  useEffect(() => { load(); }, [load]);

  // Bounce out if the round is no longer editable. PATCH would 400
  // anyway; this is friendlier.
  useEffect(() => {
    if (!round) return;
    const editable = round.status === 'draft' || round.status === 'published';
    if (!editable) {
      navigate(`/app/tournaments/${eventId}/rounds/${roundId}`, { replace: true });
    }
  }, [round, eventId, roundId, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!roundId) return;
    if (name.trim().length < 1) { setError('Round name is required.'); return; }
    const ppm = parseFloat(pointsPerMatch);
    if (!Number.isFinite(ppm) || ppm <= 0) { setError('Points per match must be a positive number.'); return; }
    if (scoringMode === 'stableford' && !stablefordValid) {
      setError('Finish the Stableford point table before saving.');
      return;
    }
    let pct: number | null = null;
    if (allowancePct.trim()) {
      const p = parseFloat(allowancePct);
      if (!Number.isFinite(p) || p < 0 || p > 200) { setError('Allowance % must be between 0 and 200.'); return; }
      pct = p;
    }
    const lowPct = parseFloat(scrambleLowPlayerPct);
    const highPct = parseFloat(scrambleHighPlayerPct);
    if (formatType === 'scramble_match_play' && (
      !Number.isFinite(lowPct) || !Number.isFinite(highPct)
      || lowPct < 0 || lowPct > 100 || highPct < 0 || highPct > 100
      || lowPct + highPct > 100
    )) {
      setError('Scramble percentages must each be between 0 and 100 and total no more than 100.');
      return;
    }

    setSaving(true);
    try {
      await api.updateTournamentRound(roundId, {
        name: name.trim(),
        sessionType,
        formatType,
        scoringMode,
        ...(scoringMode === 'stableford' ? { stablefordConfig } : {}),
        pointsPerMatch: ppm,
        // Date + optional session, encoded in local time so the picked
        // calendar day survives every timezone (the old UTC-midnight
        // write displayed as the day before in US timezones).
        scheduledStartAt: scheduledStartAt ? encodeRoundSchedule(scheduledStartAt, daySession) : null,
        handicapMethod: handicapMethod === 'inherit' ? null : handicapMethod,
        allowancePct: pct,
        baselineMethod: baselineMethod === 'inherit' ? null : baselineMethod,
        scrambleLowPlayerPct: lowPct,
        scrambleHighPlayerPct: highPct,
      });
      navigate(`/app/tournaments/${eventId}/rounds/${roundId}`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save round.');
      setSaving(false);
    }
  }

  function selectFormat(next: FormatType) {
    setFormatType(next);
    if (scoringMode === 'stableford' && next !== 'aggregate_match_play') setScoringMode('net');
  }

  function selectScoringMode(next: ScoringMode) {
    setScoringMode(next);
    if (next === 'stableford') setFormatType('aggregate_match_play');
  }

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}/rounds/${roundId}`, label: 'Round' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (!round) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}/rounds`, label: 'Rounds' }}>
        <div className="t-empty">
          <div className="t-empty-title">Round not found</div>
          <div className="t-empty-hint">{error ?? "We couldn't load this round."}</div>
        </div>
      </AppShell>
    );
  }

  const eventDefault = activeEvent?.defaultHandicapMethod ?? 'course';
  const eventAllowance = activeEvent?.defaultAllowancePct ?? 100;
  const eventBaseline = activeEvent?.defaultBaselineMethod ?? 'low_man_zero';

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}/rounds/${roundId}`, label: 'Round' }}>
      <section className="t-hero">
        <h1>Edit round setup</h1>
        <p>
          Tweak the format, scoring rules, or handicap settings for this round.
          Course and tee are locked once the round is created; recreate the round if you need to change them.
        </p>
      </section>

      {error && <div className="t-error">{error}</div>}

      <form className="t-form" onSubmit={handleSubmit}>
        <div className="t-field">
          <label htmlFor="r-name" className="t-label">Round name</label>
          <input
            id="r-name"
            className="t-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
          />
        </div>

        <div className="t-field-row">
          <div className="t-field">
            <label htmlFor="r-session" className="t-label">Session</label>
            <select
              id="r-session"
              className="t-input"
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value as RoundSessionType)}
              style={{ appearance: 'auto', paddingRight: 10 }}
            >
              {(Object.keys(SESSION_LABELS) as RoundSessionType[]).map((k) => (
                <option key={k} value={k}>{SESSION_LABELS[k]}</option>
              ))}
            </select>
          </div>
          <div className="t-field">
            <label htmlFor="r-start" className="t-label">Scheduled date</label>
            <input
              id="r-start"
              type="date"
              className="t-input"
              value={scheduledStartAt}
              onChange={(e) => setScheduledStartAt(e.target.value)}
              onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker(); } catch {} }}
            />
          </div>
        </div>

        {/* Optional tee session — mirrors CreateRound. "Default" means
            no label on the round card; Morning/Afternoon adds one. */}
        <div className="t-field">
          <div className="t-label">Tee session (optional)</div>
          <div className="t-chip-row">
            {([
              { key: 'default',   label: 'Default'   },
              { key: 'morning',   label: 'Morning'   },
              { key: 'afternoon', label: 'Afternoon' },
            ] as { key: RoundSession; label: string }[]).map((s) => (
              <button
                key={s.key}
                type="button"
                className={`t-chip ${daySession === s.key ? 't-chip-active' : ''}`}
                onClick={() => setDaySession(s.key)}
                disabled={!scheduledStartAt && s.key !== 'default'}
                title={!scheduledStartAt && s.key !== 'default' ? 'Pick a date first' : undefined}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="t-field">
          <div className="t-label">Format</div>
          <div className="t-chip-row">
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`t-chip ${formatType === opt.value ? 't-chip-active' : ''}`}
                onClick={() => selectFormat(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="t-field">
          <div className="t-label">Scoring mode</div>
          <div className="t-chip-row">
            {(Object.keys(SCORING_MODE_LABELS) as ScoringMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`t-chip ${scoringMode === mode ? 't-chip-active' : ''}`}
                onClick={() => selectScoringMode(mode)}
              >
                {SCORING_MODE_LABELS[mode]}
              </button>
            ))}
          </div>
          {scoringMode === 'stableford' ? <p className="t-hint">Stableford uses Aggregate, includes every assigned player, and lets each group save its own scores.</p> : null}
        </div>

        {scoringMode === 'stableford' ? (
          <div className="t-field scorecard-stableford-card">
            <div className="t-label">Team Stableford points</div>
            <TeamStablefordSettings
              value={stablefordConfig}
              onChange={setStablefordConfig}
              onValidityChange={setStablefordValid}
            />
          </div>
        ) : null}

        {formatType === 'scramble_match_play' && (
          <div className="t-field">
            <div className="t-label">Scramble team handicap</div>
            <div className="t-hint" style={{ marginBottom: 10 }}>
              Team handicap = low player % × low handicap + high player % × high handicap.
            </div>
            <div className="t-field-row">
              <label className="t-field">
                <span className="t-label">Low player %</span>
                <input
                  className="t-input"
                  inputMode="decimal"
                  value={scrambleLowPlayerPct}
                  onChange={(event) => setScrambleLowPlayerPct(event.target.value)}
                />
              </label>
              <label className="t-field">
                <span className="t-label">High player %</span>
                <input
                  className="t-input"
                  inputMode="decimal"
                  value={scrambleHighPlayerPct}
                  onChange={(event) => setScrambleHighPlayerPct(event.target.value)}
                />
              </label>
            </div>
          </div>
        )}

        {scoringMode !== 'stableford' ? <div className="t-field">
          <div className="t-label">Points per match</div>
          <div className="t-chip-row">
            {POINTS_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={`t-chip ${parseFloat(pointsPerMatch) === p ? 't-chip-active' : ''}`}
                onClick={() => setPointsPerMatch(String(p))}
              >
                {p}
              </button>
            ))}
            <input
              type="text"
              inputMode="decimal"
              className="t-input"
              value={pointsPerMatch}
              onChange={(e) => setPointsPerMatch(e.target.value)}
              style={{ width: 80, padding: '9px 8px', minHeight: 40 }}
            />
          </div>
        </div> : null}

        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Handicap method
            <InfoHint title="Handicap method" body={<MethodHintBody />} />
          </div>
          <div className="t-chip-row">
            <button
              type="button"
              className={`t-chip ${handicapMethod === 'inherit' ? 't-chip-active' : ''}`}
              onClick={() => setHandicapMethod('inherit')}
            >
              Inherit event default
            </button>
            {HANDICAP_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`t-chip ${handicapMethod === opt.value ? 't-chip-active' : ''}`}
                onClick={() => setHandicapMethod(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="t-field">
          <label htmlFor="r-allowance" className="t-label">
            Allowance % (blank = inherit {eventAllowance}%)
          </label>
          <input
            id="r-allowance"
            className="t-input"
            value={allowancePct}
            onChange={(e) => setAllowancePct(e.target.value)}
            inputMode="decimal"
            placeholder={String(eventAllowance)}
          />
        </div>

        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Baseline (netting)
            <InfoHint title="Baseline (netting)" body={<BaselineHintBody />} />
          </div>
          <div className="t-chip-row">
            <button
              type="button"
              className={`t-chip ${baselineMethod === 'inherit' ? 't-chip-active' : ''}`}
              onClick={() => setBaselineMethod('inherit')}
            >
              {/* Short label — the old "Inherit event default (Low man
                  zero)" was ~280px wide and wrapped inside the pill on
                  320px phones. */}
              Inherit
              <span style={{ marginLeft: 6, color: 'var(--text-mut)' }}>
                ({eventBaseline === 'low_man_zero' ? 'Low man zero' : 'Full difference'})
              </span>
            </button>
            {BASELINE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`t-chip ${baselineMethod === opt.value ? 't-chip-active' : ''}`}
                onClick={() => setBaselineMethod(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            className="t-btn t-btn-ghost"
            onClick={() => navigate(`/app/tournaments/${eventId}/rounds/${roundId}`)}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="t-btn t-btn-primary" disabled={saving} style={{ minWidth: 130 }}>
            {saving ? 'Saving…' : <><Save width={14} height={14} /> Save Setup</>}
          </button>
        </div>
      </form>
    </AppShell>
  );
}
